import { ChangeDetectorRef, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { RestErrorManager } from '../../angular-common/errors/rest-error-manager';
import { decodeUpdatableInput, FileTransfer } from '../../angular-common/util';
import { FileDto, FilterCriterion, GridAction, SortCriterion } from '../base-types';
import { BaseView } from '../base-view';
import { GridColumn } from './grid-column';
import { GridSettings } from './grid-settings';
import { GridEvent, ParentControlledGridFields } from './grid/grid.component';
import { Observable } from 'rxjs';

/**
 * Per i grid consumer che NON occupano l'intera page. 
 * Esempi sono pagine con più griglie o griglie all'interno di modali 
 */
@Component({ template: '' })
export abstract class GridConsumerComponent extends BaseView {

  /**
   * @valueForInput
   * Descrittore delle colonne del datagrid
   */
  columns: {[key: string]: GridColumn} = {}
  
  /**
   * @valueForInput
   * I settings del datagrid
   */
  _settings: GridSettings = {}
  set settings(settings: GridSettings){
    this._settings = settings
  }
  get settings(){
    return this._settings
  }

  /**
   * @valueForInput
   * Le righe contate (sottoposte a limit) del datagrid
   */
  rowCount: number;

  /**
   * @valueForInput
   * Le righe del datagrid
   */
   rows: {[key: string]: number | string | boolean | (string |number)[]  | File | File[]}[]

  /**
   * @valueForInput
   * Set di campi da passare alla griglia che devono essere controllati dal consumer
   */
  parentControlledFields : ParentControlledGridFields = {}

  /**
   * True se la griglia è in fase di caricamento (non solo gli items)
   */
  gridLoading: boolean;

  /**
   * I criteri di filtro correnti. Indispensabili in caso di griglia con mode=='server'
   */
  filterCriteria: {[id: string]: FilterCriterion} = {}

  /**
   * Il criterio di ordinamento corrente. Indispensabile in caso di griglia con mode=='server'
   */
  sortCriterion: SortCriterion

  /**
   * La riga correntemente selezionata
   */
  selectedRow: any

  /**
   * Gli id delle righe correntemente selezionate in maniera multipla
   */
  checkedRowsIds: (number|string)[]

  /**
   * Azione di griglia in corso (more button)
   */
  public currentAction: 'none' | GridAction = 'none';

  constructor(
    protected _injector: Injector
  ) { 
    super(_injector)
  }


  

  _input: any;
  /**
   * @updatableInput
   */
  @Input()
  set input(_input) {
    this._input = decodeUpdatableInput(_input)
    this._manageInputHook();
  }
  get input() {return this._input}
  protected /*abstract*/ async _manageInputHook(){}

  @Output()
  output: EventEmitter<any> = new EventEmitter<any>();
  protected /*abstract*/ async _manageOutputHook(){}

  /** @override */
  async init() {
    console.log('%clifecycle - GridConsumerComponent.init()', 'color: red;');
    console.log('%clifecycle - GridConsumerComponent.init() - updateLoading(true)', 'color: red;');
    this.gridLoading=true;
    this.updateLoading(true);

    console.log('%clifecycle - GridConsumerComponent.init() - 1: initColumns START', 'color: red;');
    await this.initColumns()
    console.log('%clifecycle - GridConsumerComponent.init() - 1: initColumns END', 'color: red;');
    console.log('%clifecycle - GridConsumerComponent.init() - 2: initSettings START', 'color: red;');
    await this.initSettings()
    console.log('%clifecycle - GridConsumerComponent.init() - 2: initSettings END', 'color: red;');
    // se mode == 'client' il datasoruce viene passato solo una volta in fase di init, perchè i filtri vengono applicati client side direttamente dentro la griglia
    // se mode == 'server' il datasource viene passato attraverso onEventEmission() ogni volta che cambia un filtro (la prima volta quando i filtri vengono inizializzati)
    if(this.settings.mode == 'client') {
      console.log('%clifecycle - GridConsumerComponent.init() - 3: setDatasource START', 'color: red;');
      await this.setDatasource()
      console.log('%clifecycle - GridConsumerComponent.init() - updateFiltering(false)', 'color: red;');
      this.updateFiltering(false)
      console.log('%clifecycle - GridConsumerComponent.init() - 3: setDatasource END', 'color: red;');
    }
  }

  /** @templateMethod */
  async initSettings() {
    this.settings = await this._initSettingsHook();
  }
  protected abstract async _initSettingsHook(): Promise<GridSettings>

  /** @templateMethod */
  async initColumns() {
    this.columns = await this._initColumnsHook()
  }
  protected abstract async _initColumnsHook(): Promise<{[key: string]: GridColumn}>

  /** @templateMethod */
  async setDatasource() : Promise<void | 'VALIDATION_ERROR' | 'JUICE_SERVER_ERROR'| 'GENERIC_ERROR'>{
    try {
        if(this.settings.mode == 'server') {
          console.log('%clifecycle - GridConsumerComponent.setDatasource() - _setRowCountHook()', 'color: red;');
          this.rowCount = await this._setRowCountHook();
          this.updateRowCount(this.rowCount);
        }
        console.log('%clifecycle - GridConsumerComponent.setDatasource() - _setDatasourceHook()', 'color: red;');
        this.rows = await this._setDatasourceHook()
        console.log('%clifecycle - GridConsumerComponent.setDatasource() - updateLoading(false)', 'color: red;');
        this.gridLoading=false;
        this.updateLoading(false);
        //await wait(1000);
        console.log('%clifecycle - GridConsumerComponent.setDatasource() - updateError(false)', 'color: red;');
        this.updateError(false);
    } catch (error) {
        //this.updateError(true);
        return this._injector.get(RestErrorManager).manageError(error);
    }
  }
  protected abstract async _setRowCountHook(limit?:boolean): Promise<number>;
  protected abstract async _setDatasourceHook(): Promise<{ [key: string]: number | string | boolean | (string | number)[] | File | File[] }[]>;

  /** @templateMethod */
  async appendRowsToDatasource(lastId: number) {
    console.log('!!!! appendRowsToDatasource')
    this.rows = this.rows.concat(await this._appendRowsToDatasourceHook(lastId));
    console.log(`caricate ${this.rows.length} righe di ${this.parentControlledFields.rowCount}`);
  }
  protected abstract async _appendRowsToDatasourceHook(lastId: number): Promise<{ [key: string]: number | string | boolean | (string | number)[] | File | File[] }[]>;

  //non mettiamo abstact per evitare di dover implementare il metodo ovunque
  protected _exportHook(): Observable<FileTransfer> {return null}

  timeoutReference;
  lastTimeoutReferenceId: number;
  async onEventEmission(event: GridEvent){
    console.log(`%clifecycle - GridConsumerComponent.onEventEmission(${Object.keys(event)[0]})`, 'color: red;');
    if(event) {
      if(![null,undefined].includes(event.moreItemsRequest)){
        console.log('GridConsumerComponent.onMoreItemsRequest :', event.moreItemsRequest);  
        this.updateLoadingMoreItems(true)
        console.log('lastId :', event.moreItemsRequest);
        await this.appendRowsToDatasource(event.moreItemsRequest);
        this.updateLoadingMoreItems(false)
      }
      if(![null,undefined].includes(event.searchCriteriaChange)){
        const timeoutId: number = new Date().getTime()
        this.lastTimeoutReferenceId = timeoutId;
        console.log(`%clifecycle - GridConsumerComponent.onEventEmission(${Object.keys(event)[0]}) - updateFiltering(true)`, 'color: red;');
        this.updateFiltering(true);
        //se arriva un nuovo valore entro il timeout precedente, viene preso in considerazione solo l'ultimo
        clearInterval(this.timeoutReference);
        this.timeoutReference = setTimeout(async (currentTimeoutId:number)=>{
            this.filterCriteria = event.searchCriteriaChange.filter;
            this.sortCriterion = event.searchCriteriaChange.sort;
            console.log(`%clifecycle - GridConsumerComponent.onEventEmission(${Object.keys(event)[0]}) - setDatasource()`, 'color: red;');
            await this.setDatasource();
            //se non ci sono altri filtri in attesa di essere applicati, allora porta a false il flag filtering, in modo che la griglia tolga lo spinner
            if(currentTimeoutId==this.lastTimeoutReferenceId) {
              console.log(`%clifecycle - GridConsumerComponent.onEventEmission(${Object.keys(event)[0]}) - updateFiltering(false)`, 'color: red;');
              this.updateFiltering(false);
            }else{
              console.log(`%clifecycle - GridConsumerComponent.onEventEmission(${Object.keys(event)[0]}) - !!!!!!!!! ricevuto updateFiltering ad esecuzione di setTimeout in corso: cosa comporta?`, 'color: red;');
            }
        },1000,timeoutId)
      }
      if(![null,undefined].includes(event.actionChange)){
        this.currentAction = event.actionChange=='none'?'none': event.actionChange
      }
      if(![null,undefined].includes(event.refreshRequest)){
        this.updateFiltering(true);
        await this.setDatasource();
        this.updateFiltering(false);
      }
      if(![null,undefined].includes(event.rowClick)){
        this.selectedRow = Object.values(event)[0]
        this.output.emit(event.rowClick)
      }
      if(![null,undefined].includes(event.checkedRowsChange)){
        this.checkedRowsIds = Object.values(event)[0]
      }
    }
  }

  /** Da richiamare da un grid consumer con mode 'server' per cambiare il rowCount quando
   * viene ricalcolato il datasource
   */
  updateRowCount = (rowCount: number) => {
    this._injector.get(ChangeDetectorRef).detectChanges();
    this.parentControlledFields = {rowCount: rowCount}
    this._injector.get(ChangeDetectorRef).detectChanges();
  }

  /** Da richiamare da un grid consumer per cambiare lo stato di caricamento della griglia (non solo dei dati)
   * 
   */
  updateLoading = (loading: boolean) => {
    this._injector.get(ChangeDetectorRef).detectChanges();
    this.parentControlledFields = {loading: loading}
    this._injector.get(ChangeDetectorRef).detectChanges();
  }

  /** Da richiamare da un grid consumer per settare lo stato di errore sul popolamento della grid
   *  Se la grid ha il flag error a true, mostra come unica possibilità all'utente il bottone refresh
   */
  updateError = (error: boolean) => {
    this._injector.get(ChangeDetectorRef).detectChanges();
    this.parentControlledFields = {error: error}
    this._injector.get(ChangeDetectorRef).detectChanges();
  }

  /** Da richiamare da un grid consumer con mode 'server' per cambiare cambiare il flag
   * che indica se ci sono dei filtri/ordinamenti in fase di applicazione (per far girare lo spinner)
   */
  updateFiltering = (filtering: boolean) => {
    //this._injector.get(ChangeDetectorRef).detectChanges();
    this.parentControlledFields = {filtering: filtering}
    //this._injector.get(ChangeDetectorRef).detectChanges();
  }

  /** Da richiamare da un grid consumer con mode 'server' per cambiare cambiare il flag
   * che indica se ci sono degli ordinamenti in fase di applicazione (per far girare lo spinner)
   */
  updateSorting = (sorting: boolean) => {
    this._injector.get(ChangeDetectorRef).detectChanges();
    this.parentControlledFields = {sorting: sorting}
    this._injector.get(ChangeDetectorRef).detectChanges();
  }

  /** Da richiamare da un grid consumer con mode 'server' per cambiare cambiare il flag
   * che indica se ci sono degli item in attesa di essere accodati al dataset (per far girare lo spinner)
   */
  updateLoadingMoreItems = (loadingMoreItems: boolean) => {
    console.log('!!!!!!!!gridConsumer - loadingMoreItems :>> ', loadingMoreItems);
    this._injector.get(ChangeDetectorRef).detectChanges();
    this.parentControlledFields = {loadingMoreItems: loadingMoreItems}
    this._injector.get(ChangeDetectorRef).detectChanges();
  }

  /** Da richiamare da un grid consumer per mostrare/nascondere la colonna con la checkbox di selezione multipla
   */
  updateSelectionColumnShown = (selectionColumnShown: boolean) => {
    console.log('!!!!!!!!gridConsumer - selectionColumnShown :>> ', selectionColumnShown);
    this._injector.get(ChangeDetectorRef).detectChanges();
    this.parentControlledFields = {selectionColumnShown: selectionColumnShown}
    this._injector.get(ChangeDetectorRef).detectChanges();
  }

  /** Da richiamare da un grid consumer per cambiare la larghezza utile sfruttabile
   * dalla griglia per mostrare le colonne prima di dover scrollare
   */
  updateWorkingWidth = (workingWidth: number) => {
    //this._injector.get(ChangeDetectorRef).detectChanges();
    this.parentControlledFields = {workingWidth: workingWidth}
    //this._injector.get(ChangeDetectorRef).detectChanges();
  }
}
